import { SITE_URL } from '../core/config';
import type { Language, Locale } from '../core/types';

const languagePath = (language: Language): string => {
  if (language === 'en') {
    return '/';
  }
  return `/${language}/`;
};

const localeToOg = (language: Language): string => {
  if (language === 'en') {
    return 'en_US';
  }
  if (language === 'de') {
    return 'de_DE';
  }
  return 'ru_RU';
};

const ensureMeta = (selector: string, attributes: Record<string, string>): HTMLMetaElement => {
  const existing = document.head.querySelector<HTMLMetaElement>(selector);
  if (existing) {
    return existing;
  }
  const meta = document.createElement('meta');
  Object.entries(attributes).forEach(([key, value]) => meta.setAttribute(key, value));
  document.head.append(meta);
  return meta;
};

const ensureLink = (selector: string, attributes: Record<string, string>): HTMLLinkElement => {
  const existing = document.head.querySelector<HTMLLinkElement>(selector);
  if (existing) {
    return existing;
  }
  const link = document.createElement('link');
  Object.entries(attributes).forEach(([key, value]) => link.setAttribute(key, value));
  document.head.append(link);
  return link;
};

const localizedUrl = (language: Language): string => `${SITE_URL}${languagePath(language)}`;

export const applySeo = (language: Language, locale: Locale): void => {
  document.title = locale.seo.title;

  ensureMeta('meta[name="description"]', { name: 'description' }).setAttribute('content', locale.seo.description);
  ensureMeta('meta[name="keywords"]', { name: 'keywords' }).setAttribute('content', locale.seo.keywords);
  ensureMeta('meta[property="og:type"]', { property: 'og:type' }).setAttribute('content', 'website');
  ensureMeta('meta[property="og:site_name"]', { property: 'og:site_name' }).setAttribute('content', 'vajaja.dev');
  ensureMeta('meta[property="og:title"]', { property: 'og:title' }).setAttribute('content', locale.seo.ogTitle);
  ensureMeta('meta[property="og:description"]', { property: 'og:description' }).setAttribute('content', locale.seo.ogDescription);
  ensureMeta('meta[property="og:url"]', { property: 'og:url' }).setAttribute('content', localizedUrl(language));
  ensureMeta('meta[property="og:locale"]', { property: 'og:locale' }).setAttribute('content', localeToOg(language));
  ensureMeta('meta[name="twitter:card"]', { name: 'twitter:card' }).setAttribute('content', 'summary_large_image');
  ensureMeta('meta[name="twitter:title"]', { name: 'twitter:title' }).setAttribute('content', locale.seo.ogTitle);
  ensureMeta('meta[name="twitter:description"]', { name: 'twitter:description' }).setAttribute('content', locale.seo.ogDescription);

  ensureLink('link[rel="canonical"]', { rel: 'canonical' }).setAttribute('href', localizedUrl(language));
  ensureLink('link[rel="alternate"][hreflang="en"]', { rel: 'alternate', hreflang: 'en' }).setAttribute('href', localizedUrl('en'));
  ensureLink('link[rel="alternate"][hreflang="ru"]', { rel: 'alternate', hreflang: 'ru' }).setAttribute('href', localizedUrl('ru'));
  ensureLink('link[rel="alternate"][hreflang="de"]', { rel: 'alternate', hreflang: 'de' }).setAttribute('href', localizedUrl('de'));
  ensureLink('link[rel="alternate"][hreflang="x-default"]', { rel: 'alternate', hreflang: 'x-default' }).setAttribute(
    'href',
    localizedUrl('en')
  );
};
